import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const {
    user,
    isLoggedIn
  } = useAuth();
  const navigate = useNavigate();
  // User profile state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addresses: [],
    loyaltyPoints: 0
  });
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  // New address state
  const [newAddress, setNewAddress] = useState({
    name: '',
    address: '',
    isDefault: false
  });
  // Status messages
  const [message, setMessage] = useState({
    type: '',
    text: ''
  });
  // Load user profile data
  useEffect(() => {
    if (!isLoggedIn || !user) {
      navigate('/login');
      return;
    }
    // Load profile from localStorage
    const savedProfile = localStorage.getItem(`profile_${user.email}`);
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setProfile({
        firstName: parsedProfile.firstName || '',
        lastName: parsedProfile.lastName || '',
        email: parsedProfile.email || user.email || '',
        phone: parsedProfile.contactNumber || '',
        addresses: parsedProfile.addresses || [],
        loyaltyPoints: parsedProfile.loyaltyPoints || 0
      });
    } else {
      // Initialize with data from auth context if available
      setProfile({
        firstName: user.name ? user.name.split(' ')[0] : '',
        lastName: user.name ? user.name.split(' ')[1] || '' : '',
        email: user.email || '',
        phone: '',
        addresses: [],
        loyaltyPoints: 0
      });
    }
  }, [user, isLoggedIn, navigate]);
  // Handle personal info changes
  const handlePersonalInfoChange = e => {
    const {
      name,
      value
    } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle password changes
  const handlePasswordChange = e => {
    const {
      name,
      value
    } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle new address changes
  const handleNewAddressChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  // Save personal information
  const savePersonalInfo = e => {
    e.preventDefault();
    if (!isLoggedIn || !user) {
      navigate('/login');
      return;
    }
    // Create updated profile to save
    const updatedProfile = {
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      contactNumber: profile.phone,
      addresses: profile.addresses,
      loyaltyPoints: profile.loyaltyPoints
    };
    // Save to localStorage
    localStorage.setItem(`profile_${user.email}`, JSON.stringify(updatedProfile));
    // Show success message
    setMessage({
      type: 'success',
      text: 'Personal information updated successfully!'
    });
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({
        type: '',
        text: ''
      });
    }, 3000);
  };
  // Save password changes
  const savePasswordChanges = e => {
    e.preventDefault();
    // Simple validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'New passwords do not match!'
      });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Password must be at least 6 characters!'
      });
      return;
    }
    // In a real app, you would verify the current password and update it in the backend
    // For this demo, we'll just show a success message
    setMessage({
      type: 'success',
      text: 'Password updated successfully!'
    });
    // Reset form
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({
        type: '',
        text: ''
      });
    }, 3000);
  };
  // Add a new address
  const addNewAddress = e => {
    e.preventDefault();
    if (!newAddress.name || !newAddress.address) {
      setMessage({
        type: 'error',
        text: 'Please fill out all address fields!'
      });
      return;
    }
    // Create new address object
    const addressToAdd = {
      id: Date.now(),
      name: newAddress.name,
      address: newAddress.address,
      isDefault: newAddress.isDefault || profile.addresses.length === 0 // Make first address default if none exists
    };
    // If this is set as default, update other addresses
    let updatedAddresses = [...profile.addresses];
    if (addressToAdd.isDefault) {
      // Remove default from all other addresses
      updatedAddresses = updatedAddresses.map(addr => ({
        ...addr,
        isDefault: false
      }));
    }
    // Add the new address
    updatedAddresses.push(addressToAdd);
    // Update profile
    const updatedProfile = {
      ...profile,
      addresses: updatedAddresses
    };
    setProfile(updatedProfile);
    // Save to localStorage
    localStorage.setItem(`profile_${user.email}`, JSON.stringify({
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      email: updatedProfile.email,
      contactNumber: updatedProfile.phone,
      addresses: updatedProfile.addresses,
      loyaltyPoints: updatedProfile.loyaltyPoints
    }));
    // Reset form
    setNewAddress({
      name: '',
      address: '',
      isDefault: false
    });
    // Show success message
    setMessage({
      type: 'success',
      text: 'Address added successfully!'
    });
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({
        type: '',
        text: ''
      });
    }, 3000);
  };
  // Delete address
  const deleteAddress = id => {
    const updatedAddresses = profile.addresses.filter(addr => addr.id !== id);
    // Update profile
    const updatedProfile = {
      ...profile,
      addresses: updatedAddresses
    };
    setProfile(updatedProfile);
    // Save to localStorage
    localStorage.setItem(`profile_${user.email}`, JSON.stringify({
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      email: updatedProfile.email,
      contactNumber: updatedProfile.phone,
      addresses: updatedProfile.addresses,
      loyaltyPoints: updatedProfile.loyaltyPoints
    }));
    // Show success message
    setMessage({
      type: 'success',
      text: 'Address deleted successfully!'
    });
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({
        type: '',
        text: ''
      });
    }, 3000);
  };
  // Set address as default
  const setDefaultAddress = id => {
    // Update addresses with new default
    const updatedAddresses = profile.addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === id
    }));
    // Update profile
    const updatedProfile = {
      ...profile,
      addresses: updatedAddresses
    };
    setProfile(updatedProfile);
    // Save to localStorage
    localStorage.setItem(`profile_${user.email}`, JSON.stringify({
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      email: updatedProfile.email,
      contactNumber: updatedProfile.phone,
      addresses: updatedProfile.addresses,
      loyaltyPoints: updatedProfile.loyaltyPoints
    }));
    // Show success message
    setMessage({
      type: 'success',
      text: 'Default address updated!'
    });
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage({
        type: '',
        text: ''
      });
    }, 3000);
  };
  // If not authenticated, show loading state
  if (!isLoggedIn || !user) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-1980">
        <div className="text-center" data-id="element-1981">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800 mx-auto" data-id="element-1982"></div>
          <p className="mt-4 text-amber-800" data-id="element-1983">Loading profile...</p>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen py-8" data-id="element-1984">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1985">
        <div className="mb-6" data-id="element-1986">
          <Link to="/customer" className="text-orange-600 hover:text-orange-800 flex items-center" data-id="element-1987">
            <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-1988">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" data-id="element-1989"></path>
            </svg>
            Back to Dashboard
          </Link>
        </div>
        {/* Status message */}
        {message.text && <div className={`mb-4 p-3 rounded-md ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1990">
            {message.text}
          </div>}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-1991">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-1992">
            <h1 className="text-xl font-semibold text-gray-900" data-id="element-1993">
              Your Profile
            </h1>
          </div>
          <div className="border-b border-gray-200" data-id="element-1994">
            <nav className="flex -mb-px" data-id="element-1995">
              <button onClick={() => setActiveTab('personal')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'personal' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1996">
                Personal Information
              </button>
              <button onClick={() => setActiveTab('addresses')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'addresses' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1997">
                Addresses
              </button>
              <button onClick={() => setActiveTab('password')} className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeTab === 'password' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1998">
                Change Password
              </button>
            </nav>
          </div>
          <div className="p-6" data-id="element-1999">
            {activeTab === 'personal' && <div data-id="element-2000">
                <div className="mb-6" data-id="element-2001">
                  <div className="flex items-center justify-between" data-id="element-2002">
                    <h2 className="text-lg font-medium text-gray-900" data-id="element-2003">
                      Personal Information
                    </h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2004">
                      {profile.loyaltyPoints} Loyalty Points
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-2005">
                    Update your personal details and contact information.
                  </p>
                </div>
                <form onSubmit={savePersonalInfo} data-id="element-2006">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2007">
                    <div data-id="element-2008">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2009">
                        First Name
                      </label>
                      <input type="text" id="firstName" name="firstName" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.firstName} onChange={handlePersonalInfoChange} data-id="element-2010" />
                    </div>
                    <div data-id="element-2011">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2012">
                        Last Name
                      </label>
                      <input type="text" id="lastName" name="lastName" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.lastName} onChange={handlePersonalInfoChange} data-id="element-2013" />
                    </div>
                    <div data-id="element-2014">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2015">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.email} onChange={handlePersonalInfoChange} data-id="element-2016" />
                    </div>
                    <div data-id="element-2017">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2018">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" name="phone" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" value={profile.phone} onChange={handlePersonalInfoChange} data-id="element-2019" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end" data-id="element-2020">
                    <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-2021">
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>}
            {activeTab === 'addresses' && <div data-id="element-2022">
                <div className="mb-6" data-id="element-2023">
                  <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2024">
                    Delivery Addresses
                  </h2>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-2025">
                    Manage your delivery addresses for faster checkout.
                  </p>
                  {/* Existing addresses */}
                  <div className="space-y-4 mb-6" data-id="element-2026">
                    {profile.addresses.length === 0 ? <div className="text-center py-4 text-gray-500" data-id="element-2027">
                        You haven't added any addresses yet.
                      </div> : profile.addresses.map(address => <div key={address.id} className="border border-gray-200 rounded-md p-4" data-id="element-2028">
                          <div className="flex justify-between items-start" data-id="element-2029">
                            <div data-id="element-2030">
                              <div className="flex items-center" data-id="element-2031">
                                <h3 className="font-medium text-gray-900" data-id="element-2032">
                                  {address.name}
                                </h3>
                                {address.isDefault && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2033">
                                    Default
                                  </span>}
                              </div>
                              <p className="text-sm text-gray-600 mt-1" data-id="element-2034">
                                {address.address}
                              </p>
                            </div>
                            <div className="flex space-x-2" data-id="element-2035">
                              {!address.isDefault && <button className="text-orange-600 hover:text-orange-800" onClick={() => setDefaultAddress(address.id)} data-id="element-2036">
                                  Set as Default
                                </button>}
                              <button className="text-red-600 hover:text-red-800" onClick={() => deleteAddress(address.id)} data-id="element-2037">
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>)}
                  </div>
                  {/* Add new address form */}
                  <div className="border-t border-gray-200 pt-4" data-id="element-2038">
                    <h3 className="text-md font-medium text-gray-900 mb-3" data-id="element-2039">
                      Add New Address
                    </h3>
                    <form onSubmit={addNewAddress} data-id="element-2040">
                      <div className="space-y-4" data-id="element-2041">
                        <div data-id="element-2042">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2043">
                            Address Name
                          </label>
                          <input type="text" name="name" value={newAddress.name} onChange={handleNewAddressChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" placeholder="Home, Office, etc." data-id="element-2044" />
                        </div>
                        <div data-id="element-2045">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2046">
                            Full Address
                          </label>
                          <textarea name="address" value={newAddress.address} onChange={handleNewAddressChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" placeholder="Street, Barangay, City, etc." data-id="element-2047" />
                        </div>
                        <div className="flex items-center" data-id="element-2048">
                          <input type="checkbox" id="isDefault" name="isDefault" checked={newAddress.isDefault} onChange={handleNewAddressChange} className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" data-id="element-2049" />
                          <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700" data-id="element-2050">
                            Set as default address
                          </label>
                        </div>
                        <div data-id="element-2051">
                          <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-2052">
                            Add Address
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>}
            {activeTab === 'password' && <div data-id="element-2053">
                <div className="mb-6" data-id="element-2054">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-2055">
                    Change Password
                  </h2>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-2056">
                    Update your password to keep your account secure.
                  </p>
                </div>
                <form onSubmit={savePasswordChanges} data-id="element-2057">
                  <div className="space-y-4" data-id="element-2058">
                    <div data-id="element-2059">
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2060">
                        Current Password
                      </label>
                      <input type="password" id="currentPassword" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" data-id="element-2061" />
                    </div>
                    <div data-id="element-2062">
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2063">
                        New Password
                      </label>
                      <input type="password" id="newPassword" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" data-id="element-2064" />
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2065">
                        Password must be at least 6 characters.
                      </p>
                    </div>
                    <div data-id="element-2066">
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2067">
                        Confirm New Password
                      </label>
                      <input type="password" id="confirmPassword" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500" data-id="element-2068" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end" data-id="element-2069">
                    <button type="submit" className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-2070">
                      Update Password
                    </button>
                  </div>
                </form>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default UserProfile;